import React from 'react';
import { Clock, Users, Star, Shield, MapPin } from 'lucide-react';

const ProduitFieldsCentreEquestre = ({ formData, setFormData }) => {
  const dureeOptions = [
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 heure' },
    { value: 90, label: '1h30' },
    { value: 120, label: '2 heures' },
    { value: 180, label: '3 heures' },
    { value: 240, label: '4 heures' },
    { value: 480, label: 'Demi-journée (8h)' },
    { value: 960, label: 'Journée complète (16h)' }
  ];

  const niveauOptions = [
    { value: 'DEBUTANT', label: '🟢 Débutant' },
    { value: 'INTERMEDIAIRE', label: '🟡 Intermédiaire' },
    { value: 'CONFIRME', label: '🔴 Confirmé' },
    { value: 'TOUS_NIVEAUX', label: '⚪ Tous niveaux' }
  ];

  const typeActiviteOptions = [
    { value: 'BALADE', label: '🐴 Balade' },
    { value: 'COURS', label: '📚 Cours' },
    { value: 'STAGE', label: '🏆 Stage' },
    { value: 'PENSION', label: '🏠 Pension' },
    { value: 'SOINS', label: '💆 Soins des chevaux' },
    { value: 'MANEGE', label: '🏟️ Manège' },
    { value: 'CROSS', label: '🌲 Cross' },
    { value: 'SAUT', label: '🦘 Saut d\'obstacles' }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
          <MapPin className="h-5 w-5 mr-2" />
          Configuration Centre Équestre
        </h3>

        {/* Type d'activité */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type d'activité *
          </label>
          <select
            value={formData.typeActivite || ''}
            onChange={(e) => setFormData({ ...formData, typeActivite: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Sélectionner le type d'activité</option>
            {typeActiviteOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Durée principale */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              Durée principale *
            </label>
            <select
              value={formData.dureeMinutes || ''}
              onChange={(e) => setFormData({ ...formData, dureeMinutes: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Sélectionner la durée</option>
              {dureeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Nombre max de cavaliers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <Users className="h-4 w-4 mr-1" />
              Nombre max de cavaliers
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={formData.nombreMaxCavaliers || ''}
              onChange={(e) => setFormData({ ...formData, nombreMaxCavaliers: parseInt(e.target.value) || null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: 8"
            />
          </div>

          {/* Niveau requis */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <Star className="h-4 w-4 mr-1" />
              Niveau requis
            </label>
            <select
              value={formData.niveauRequis || ''}
              onChange={(e) => setFormData({ ...formData, niveauRequis: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Sélectionner le niveau</option>
              {niveauOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Âge minimum */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <Shield className="h-4 w-4 mr-1" />
              Âge minimum (années)
            </label>
            <input
              type="number"
              min="3"
              max="99"
              value={formData.ageMinimum || ''}
              onChange={(e) => setFormData({ ...formData, ageMinimum: parseInt(e.target.value) || null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: 8"
            />
          </div>
        </div>

        {/* Variantes de durée */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            Variantes de durée disponibles
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {dureeOptions.map(option => (
              <label key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  name="dureesDisponibles"
                  value={option.value}
                  checked={formData.dureesDisponibles?.includes(option.value) || false}
                  onChange={(e) => {
                    const { value, checked } = e.target;
                    let newDurees = [...(formData.dureesDisponibles || [])];
                    if (checked) {
                      newDurees.push(parseInt(value));
                    } else {
                      newDurees = newDurees.filter(d => d !== parseInt(value));
                    }
                    setFormData({ ...formData, dureesDisponibles: newDurees });
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Cochez les durées disponibles pour cette activité (ex: Balade 1h, 2h, demi-journée)
          </p>
        </div>

        {/* Équipement fourni */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Équipement fourni
          </label>
          <div className="space-y-2">
            {[
              { key: 'equipementFourni_casque', label: 'Casque' },
              { key: 'equipementFourni_bottes', label: 'Bottes d\'équitation' },
              { key: 'equipementFourni_veste', label: 'Veste de protection' },
              { key: 'equipementFourni_selle', label: 'Selle et accessoires' },
              { key: 'equipementFourni_bride', label: 'Bride et mors' }
            ].map(equipement => (
              <label key={equipement.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData[equipement.key] || false}
                  onChange={(e) => setFormData({ ...formData, [equipement.key]: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                />
                <span className="text-sm text-gray-700">{equipement.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Informations supplémentaires */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Informations importantes
          </label>
          <textarea
            value={formData.informationsSupplementaires || ''}
            onChange={(e) => setFormData({ ...formData, informationsSupplementaires: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="3"
            placeholder="Ex: Tenue adaptée recommandée, certificat médical requis, annulation possible en cas de mauvais temps..."
          />
        </div>
      </div>
    </div>
  );
};

export default ProduitFieldsCentreEquestre;
